import React, { useState, useRef } from 'react';
import DemoBanner from '../../components/DemoBanner';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiEdit, FiArrowRight, FiCheck, FiCopy, FiUpload, 
  FiLink, FiFileText, FiTarget, FiZap, 
  FiShield, FiLayers, FiSearch, FiAlertCircle, FiUserCheck, FiBriefcase, FiHome, FiActivity
} = FiIcons;

const Motivatiebrief = () => {
  const [cvText, setCvText] = useState('');
  const [cvLink, setCvLink] = useState('');
  const [cvFileName, setCvFileName] = useState('');
  const [jobText, setJobText] = useState('');
  const [jobLink, setJobLink] = useState('');
  const [jobFileName, setJobFileName] = useState('');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [analysisError, setAnalysisError] = useState('');
  const [output, setOutput] = useState('');
  const [decisionData, setDecisionData] = useState(null);

  const cvFileRef = useRef(null);
  const jobFileRef = useRef(null);

  const steps = [
    { id: 1, label: 'FASE 1: FEITELIJKE ANALYSE', icon: FiSearch },
    { id: 2, label: 'FASE 2: OVERLAPBESLISSING', icon: FiLayers },
    { id: 3, label: 'FASE 3: POSITIONERING', icon: FiTarget },
    { id: 4, label: 'FASE 4: SCHRIJVEN', icon: FiEdit }
  ];

  const handleFileUpload = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      if (type === 'cv') { setCvFileName(file.name); setCvText(content); }
      else { setJobFileName(file.name); setJobText(content); }
    };
    reader.readAsText(file);
  };

  const handleGenerate = () => {
    setAnalysisError('');
    setOutput('');
    setDecisionData(null);
    
    const fullCv = (cvText + " " + cvLink).trim();
    const fullJob = (jobText + " " + jobLink).trim();

    if (fullCv.length < 50 || fullJob.length < 50) {
      setAnalysisError("ONVOLDOENDE DATA VOOR EEN RECRUITER-ANALYSE. VOER MEER DETAILS IN.");
      return;
    }

    setIsGenerating(true);
    setCurrentStep(1);
    
    setTimeout(() => {
      const lowCv = fullCv.toLowerCase();
      const lowJob = fullJob.toLowerCase();

      // --- FASE 1: FEITELIJKE ANALYSE (Intern) ---
      // Analyseert sector, organisatie en gedrag

      // --- FASE 2: OVERLAPBESLISSING (HARD) ---
      const overlap = {
        direct: ['ervaring', 'verantwoordelijk', 'taken', 'uitvoeren'].some(kw => lowCv.includes(kw) && lowJob.includes(kw)),
        functional: ['coördinat', 'beleid', 'kwaliteit', 'veiligheid', 'mentor', 'begeleid', 'projectleid', 'procesontwikkeling'].some(kw => lowCv.includes(kw)),
        transferable: ['onderwijs', 'zorg', 'overheid', 'tech', 'besluitvorming', 'invloed', 'impact'].some(kw => lowCv.includes(kw))
      };

      if (!overlap.direct && !overlap.functional && !overlap.transferable) {
        setAnalysisError("GEEN BRIEF: Kandidaat kan dit werk niet geloofwaardig uitvoeren op basis van gedrag uit het CV.");
        setIsGenerating(false);
        return;
      }

      setCurrentStep(2);

      // --- CONTEXT-VERPLICHTING (HARD) ---
      const contextMapping = [
        { name: 'Onderwijs', org: 'school', env: 'leerlingen/studenten', kws: ['onderwijs', 'school', 'docent', 'leerling', 'klas', 'curriculum', 'pabo'] },
        { name: 'Zorg', org: 'zorginstelling', env: 'cliënten/patiënten', kws: ['zorg', 'patiënt', 'cliënt', 'verpleeg', 'ziekenhuis', 'kliniek', 'vvt'] },
        { name: 'Overheid', org: 'overheidsinstantie', env: 'burgers/maatschappij', kws: ['gemeente', 'overheid', 'beleid', 'burger', 'ministerie', 'ambtenaar'] },
        { name: 'Bedrijfsleven', org: 'bedrijf', env: 'klanten/markt', kws: ['klant', 'sales', 'markt', 'commercieel', 'bedrijf', 'bv', 'onderneming'] }
      ];

      const detectedContext = contextMapping.find(c => c.kws.some(kw => lowJob.includes(kw)));

      if (!detectedContext) {
        setAnalysisError("NIET SCHRIJVEN: Organisatietype of werkomgeving niet expliciet herleidbaar. Heranalyseer vacature.");
        setIsGenerating(false);
        return;
      }

      setCurrentStep(3);

      // --- FASE 3: POSITIONERINGSKEUZE (Kies exact ÉÉN) ---
      let angle = "uitvoerend vakmanschap";
      if (overlap.functional && (lowJob.includes('leiden') || lowJob.includes('verantwoordelijk'))) {
        angle = "functioneel leiderschap";
      } else if (lowCv.includes('specialist') || lowCv.includes('expert')) {
        angle = "inhoudelijke expertise";
      } else if (overlap.transferable && !overlap.direct) {
        angle = "groei naar leiderschap";
      } else if (lowJob.includes('coördineren')) {
        angle = "coördinerende kracht";
      }

      setDecisionData({ 
        angle, 
        context: detectedContext,
        bedrijf: fullJob.match(/(?:bij|voor|van)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/)?.[1] || `uw ${detectedContext.org}`,
        functie: fullJob.match(/(?:vacature|functie|rol)\s+(?:van|voor|als)?\s+([A-Z][a-z]+(?:\s+[a-z]+)*)/i)?.[1] || "deze rol"
      });

      setCurrentStep(4);

      // --- FASE 4: SCHRIJVEN ---
      setTimeout(() => {
        const brief = generateExpertBrief(angle, detectedContext, fullJob);
        setOutput(brief);
        setIsGenerating(false);
        setCurrentStep(0);
      }, 1500);

    }, 1500);
  };

  const generateExpertBrief = (angle, context, job) => {
    const bedrijfMatch = job.match(/(?:bij|voor|van)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/);
    const bedrijf = bedrijfMatch ? bedrijfMatch[1] : `uw ${context.org}`;
    const functieMatch = job.match(/(?:vacature|functie|rol)\s+(?:van|voor|als)?\s+([A-Z][a-z]+(?:\s+[a-z]+)*)/i);
    const functie = functieMatch ? functieMatch[1] : "deze rol";

    const content = {
      "uitvoerend vakmanschap": {
        intro: `Uw ${context.org}, ${bedrijf}, zoekt een ${functie} die direct waarde toevoegt voor de ${context.env}. Mijn trackrecord laat zien dat ik situaties niet alleen begrijp, maar ze ook feitelijk oplos binnen de ${context.name}-sector.`,
        body: `Waar u vraagt om een proactieve aanpak binnen deze ${context.org}, breng ik de executiekracht die ik eerder heb bewezen door verantwoordelijkheid te nemen voor de dagelijkse resultaten en de kwaliteit van de dienstverlening aan ${context.env}.`
      },
      "functioneel leiderschap": {
        intro: `De rol van ${functie} bij ${bedrijf} vraagt om iemand die de koers bewaakt en teams stuurt in een complexe ${context.name}-omgeving. Ik breng de stabiliteit en de sturende kracht die nodig is om de belangen van de ${context.env} te borgen.`,
        body: `In mijn eerdere rollen heb ik aangetoond dat ik leiderschap neem over processen en kwaliteit, juist in een ${context.org} waar de druk op de ${context.env} hoog is. Ik focus op het creëren van eigenaarschap.`
      },
      "groei naar leiderschap": {
        intro: `De ambitie van ${bedrijf} vraagt om een ${functie} die bereid is de volgende stap te zetten binnen de ${context.name}-sector. Hoewel mijn achtergrond in een andere context ligt, is de overdraagbare verantwoordelijkheid voor ${context.env} die ik heb gedragen de ideale basis.`,
        body: `Ik heb bewezen dat ik binnen een ${context.org} invloed kan uitoefenen zonder formele titel, door simpelweg de regie te pakken wanneer de context daarom vraagt.`
      }
    };

    const selected = content[angle] || content["uitvoerend vakmanschap"];

    return `${selected.intro}

In mijn eerdere werkervaring heb ik aangetoond dat ik verantwoordelijkheid neem waar anderen stoppen. ${selected.body} Mijn aanpak is gebaseerd op de feitelijke executie die nodig is om successen in de ${context.name}-sector te borgen en de continuïteit voor de ${context.env} te garanderen.

Wat mij onderscheidt als professional, is het vermogen om in een veranderende omgeving de focus te houden op wat er echt toe doet: de kwaliteit richting de ${context.env}. Ik zie in uw ${context.org} een sterke overlap met mijn eigen werkwijze, waarbij ik situaties die vragen om direct handelen altijd combineer met een analytische blik op de lange termijn.

Mijn trackrecord laat zien dat ik me nieuwe domeinen en complexe vraagstukken binnen de ${context.name}-context sneller eigen maak dan gemiddeld. Ik ben gewend om de regie te pakken wanneer de veiligheid of de voortgang van de ${context.env} daarom vraagt.

Graag bespreek ik in een persoonlijk gesprek hoe mijn specifieke positionering als professional en mijn bewezen werkwijze kunnen bijdragen aan de ambities van ${bedrijf}.

Met vriendelijke groet,

[Jouw Naam]`;
  };

  const isValid = (cvText.trim() || cvLink.trim() || cvFileName) && (jobText.trim() || jobLink.trim() || jobFileName);

  return (
    <div className="min-h-screen py-16 bg-gray-50 font-inter">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <DemoBanner />
        
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-full text-[10px] font-black uppercase tracking-[0.25em] mb-6 shadow-xl border border-white/10">
            <SafeIcon icon={FiShield} className="text-brand-orange" /> Recruiter Persona ChatGPT-8.0
          </div>
          <h1 className="text-5xl font-extrabold text-brand-dark mb-4 tracking-tight italic">Expert <span className="text-brand-orange">Motivatie</span> Engine</h1>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed font-medium italic">
            Niet "een brief", maar een besluit of iemand geloofwaardig kan solliciteren. <br/>
            Harde context-verplichting en gedragsanalyse.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-10">
          <div className="bg-white p-7 rounded-3xl border border-gray-200 shadow-sm flex flex-col h-full ring-1 ring-gray-100/50">
            <label className="font-black text-brand-dark uppercase tracking-widest text-[11px] flex items-center gap-2 mb-6">
              <SafeIcon icon={FiBriefcase} className="text-brand-orange" /> CV Analyse (Gedrag)
            </label>
            <div className="space-y-4 flex-1">
              <div onClick={() => cvFileRef.current.click()} className="w-full py-4 border-2 border-dashed border-gray-100 rounded-2xl flex items-center justify-center gap-3 text-gray-400 text-xs font-bold cursor-pointer hover:bg-gray-50 transition-all">
                <SafeIcon icon={FiUpload} /> {cvFileName || 'Upload CV'}
              </div>
              <input type="file" ref={cvFileRef} onChange={(e) => handleFileUpload(e, 'cv')} className="hidden" />
              <textarea value={cvText} onChange={(e) => setCvText(e.target.value)} className="w-full h-48 p-4 border border-gray-50 rounded-2xl text-[11px] bg-gray-50/30 outline-none focus:bg-white resize-none" placeholder="Wat deed deze persoon concreet?" />
            </div>
          </div>

          <div className="bg-white p-7 rounded-3xl border border-gray-200 shadow-sm flex flex-col h-full ring-1 ring-gray-100/50">
            <label className="font-black text-brand-dark uppercase tracking-widest text-[11px] flex items-center gap-2 mb-6">
              <SafeIcon icon={FiTarget} className="text-brand-orange" /> Vacature Analyse (Context)
            </label>
            <div className="space-y-4 flex-1">
              <div onClick={() => jobFileRef.current.click()} className="w-full py-4 border-2 border-dashed border-gray-100 rounded-2xl flex items-center justify-center gap-3 text-gray-400 text-xs font-bold cursor-pointer hover:bg-gray-50 transition-all">
                <SafeIcon icon={FiUpload} /> {jobFileName || 'Upload Vacature'}
              </div>
              <input type="file" ref={jobFileRef} onChange={(e) => handleFileUpload(e, 'job')} className="hidden" />
              <textarea value={jobText} onChange={(e) => setJobText(e.target.value)} className="w-full h-48 p-4 border border-gray-50 rounded-2xl text-[11px] bg-gray-50/30 outline-none focus:bg-white resize-none" placeholder="Wat mag hier NIET fout gaan?" />
            </div>
          </div>
        </div>

        {isGenerating ? (
          <div className="bg-brand-dark p-10 rounded-[2.5rem] mb-12 shadow-2xl relative overflow-hidden">
            <div className="flex justify-between mb-0 gap-2 relative z-10">
              {steps.map((step) => (
                <div key={step.id} className={`flex flex-col items-center gap-3 flex-1 transition-all duration-700 ${currentStep >= step.id ? 'opacity-100' : 'opacity-20'}`}>
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${currentStep === step.id ? 'bg-brand-orange text-white scale-110 shadow-lg' : 'bg-white/5 text-white/40'}`}>
                    <SafeIcon icon={step.icon} className="w-6 h-6" />
                  </div>
                  <span className="text-[9px] font-black text-white uppercase tracking-widest text-center hidden sm:block">{step.label}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-16">
            <button onClick={handleGenerate} disabled={!isValid} className={`w-full py-6 rounded-[1.5rem] font-black text-white transition-all flex items-center justify-center gap-4 shadow-2xl ${!isValid ? 'bg-gray-300' : 'bg-brand-dark hover:bg-black hover:-translate-y-1'}`} >
              <span className="text-xl uppercase tracking-widest">Start Recruiter Analyse</span>
              <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
            </button>
            {analysisError && (
              <div className="bg-red-50 border border-red-100 p-6 rounded-2xl mt-8 flex items-center gap-4 animate-shake">
                <SafeIcon icon={FiAlertCircle} className="text-red-500 w-6 h-6 flex-shrink-0" />
                <p className="text-red-800 text-xs font-black uppercase tracking-widest italic">{analysisError}</p>
              </div>
            )}
          </div>
        )}

        {output && !isGenerating && decisionData && (
          <div className="space-y-10 animate-fade-in pb-32">
            <div className="bg-white p-8 rounded-[2rem] border border-gray-200 shadow-sm grid md:grid-cols-3 gap-6">
              <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Organisatie</p>
                <p className="text-md font-black text-brand-dark uppercase italic">{decisionData.context.org}</p>
              </div>
              <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Invalshoek</p>
                <p className="text-md font-black text-brand-dark uppercase italic">{decisionData.angle}</p>
              </div>
              <div className="p-5 bg-gray-50 rounded-2xl border border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Omgeving</p>
                <p className="text-md font-black text-brand-orange uppercase italic">{decisionData.context.env}</p>
              </div>
            </div>

            <div className="bg-white p-12 rounded-[2.5rem] border-2 border-brand-dark shadow-2xl relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-8 py-2 bg-brand-dark text-white text-[10px] font-black uppercase tracking-[0.4em] rounded-full">
                Professional Copywriter Output
              </div>
              <div className="flex justify-between items-center mb-12 border-b border-gray-100 pb-8 mt-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-orange rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <SafeIcon icon={FiZap} className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-brand-dark tracking-tighter uppercase italic">Geloofwaardige Brief</h2>
                    <p className="text-[11px] text-gray-400 font-bold uppercase tracking-widest italic">Context-Locked • Gedragsgericht</p>
                  </div>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(output); alert('Gekopieerd!'); }} className="bg-brand-dark text-white p-4 rounded-xl font-bold hover:bg-brand-orange transition-all shadow-lg">
                  <SafeIcon icon={FiCopy} className="w-5 h-5" />
                </button>
              </div>
              <div className="bg-white text-gray-800 leading-[1.6] whitespace-pre-wrap text-[17px] px-4 selection:bg-brand-orange/20" style={{ fontFamily: 'Calibri, "Gill Sans", "Gill Sans MT", "Trebuchet MS", sans-serif' }}>
                {output}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Motivatiebrief;